import { Share, X } from 'lucide-react';
import { useIOSInstallHint } from '@/lib/pwa';

/**
 * iOS Safari "Add to Home Screen" hint.
 *
 * iOS gives web apps no programmatic install — the only route is the user
 * manually choosing "Add to Home Screen" from Safari's Share menu. This
 * banner therefore only INSTRUCTS; it is deliberately not an action card.
 *
 * Earlier styling mirrored the actionable Android `InstallPrompt` (cream
 * card + bold icon tile), so users tapped the card expecting something to
 * happen — and on iOS nothing programmatic can. This version reads plainly
 * as a tip: the instruction is the content, the Share glyph is shown inline
 * so the icon is recognisable in Safari's toolbar, and the only control is
 * the dismiss (X). Tapping the card body is intentionally inert.
 */
export function IOSInstallHint() {
  const { visible, dismiss } = useIOSInstallHint();
  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="How to add TownInsider to your Home Screen"
      className="fixed inset-x-3 bottom-3 z-30 mx-auto max-w-md rounded-2xl bg-cream-100 px-4 py-3 text-moor-800 shadow-lg ring-1 ring-moor-200/60 safe-bottom animate-slide-up"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded-full p-1 text-moor-500 transition hover:bg-moor-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <p className="pr-6 text-sm font-medium leading-snug">
        Keep TownInsider on your Home Screen
      </p>
      <p className="mt-1 pr-6 text-sm leading-snug text-moor-700">
        In Safari, tap the Share icon
        <Share
          aria-hidden="true"
          className="mx-1 inline-block h-4 w-4 -translate-y-px align-middle"
        />
        in the toolbar, then choose{' '}
        <span className="font-medium">Add to Home Screen</span>.
      </p>
    </div>
  );
}
